import moment from 'moment-timezone'
import store from '@/store'

/**
 * 获取服务器时区
 * @returns {string} 服务器时区，默认为 'Asia/Shanghai'
 */
export function getServerTimezone() {
  // 优先用服务端下发的时区；未就绪时退回"浏览器本地时区"（而非硬编码上海），
  // 避免在配置加载完成前用一个固定的错误时区去计算时间窗口。
  return store.state.timeZone || moment.tz.guess() || 'Asia/Shanghai'
}

/**
 * 获取服务器当前时间
 * @returns {moment.Moment} 服务器时区的当前时间
 */
export function getServerNow() {
  const serverTimezone = getServerTimezone()
  const offset = store.state.serverTimeOffsetMs || 0
  return moment(Date.now() + offset).tz(serverTimezone)
}

/**
 * 将本地时间转换为服务器时间
 * @param {string|Date|moment.Moment} localTime 本地时间
 * @returns {moment.Moment} 服务器时区的时间
 */
export function localToServerTime(localTime) {
  const serverTimezone = getServerTimezone()
  return moment(localTime).tz(serverTimezone)
}

/**
 * 将服务器时间转换为本地时间
 * @param {string|Date|moment.Moment} serverTime 服务器时间
 * @returns {moment.Moment} 本地时区的时间
 */
export function serverToLocalTime(serverTime) {
  const serverTimezone = getServerTimezone()
  return moment.tz(serverTime, serverTimezone).local()
}

/**
 * 获取服务器时区的今天开始时间（00:00:00）
 * @returns {string} 格式化的时间字符串 'YYYY-MM-DD 00:00:00'
 */
export function getServerTodayStart() {
  const serverNow = getServerNow()
  return serverNow.format('YYYY-MM-DD') + ' 00:00:00'
}

/**
 * 获取服务器时区的今天结束时间（23:59:59）
 * @returns {string} 格式化的时间字符串 'YYYY-MM-DD 23:59:59'
 */
export function getServerTodayEnd() {
  const serverNow = getServerNow()
  return serverNow.format('YYYY-MM-DD') + ' 23:59:59'
}

/**
 * 获取服务器时区的指定日期开始时间（00:00:00）
 * @param {string|Date|moment.Moment} date 指定日期
 * @returns {string} 格式化的时间字符串 'YYYY-MM-DD 00:00:00'
 */
export function getServerDateStart(date) {
  const serverTimezone = getServerTimezone()
  const serverDate = moment.tz(date, serverTimezone)
  return serverDate.format('YYYY-MM-DD') + ' 00:00:00'
}

/**
 * 获取服务器时区的指定日期结束时间（23:59:59）
 * @param {string|Date|moment.Moment} date 指定日期
 * @returns {string} 格式化的时间字符串 'YYYY-MM-DD 23:59:59'
 */
export function getServerDateEnd(date) {
  const serverTimezone = getServerTimezone()
  const serverDate = moment.tz(date, serverTimezone)
  return serverDate.format('YYYY-MM-DD') + ' 23:59:59'
}

/**
 * 格式化服务器时间
 * @param {string|Date|moment.Moment} serverTime 服务器时间
 * @param {string} format 格式化字符串，默认为 'YYYY-MM-DD HH:mm:ss'
 * @returns {string} 格式化后的时间字符串
 */
export function formatServerTime(serverTime, format = 'YYYY-MM-DD HH:mm:ss') {
  const serverTimezone = getServerTimezone()
  if (typeof serverTime === 'number') {
    return moment(serverTime).tz(serverTimezone).format(format)
  }
  return moment.tz(serverTime, serverTimezone).format(format)
}

export function formatServerTimeEn(serverTime, format = 'YYYY-MM-DD HH:mm:ss') {
  const serverTimezone = getServerTimezone()
  if (typeof serverTime === 'number') {
    return moment(serverTime).tz(serverTimezone).locale('en').format(format)
  }
  return moment.tz(serverTime, serverTimezone).locale('en').format(format)
}

/**
 * 计算服务器时间差（秒）
 * @param {string|Date|moment.Moment} startTime 开始时间
 * @param {string|Date|moment.Moment} endTime 结束时间，默认为当前服务器时间
 * @returns {number} 时间差（秒）
 */
export function getServerTimeDiff(startTime, endTime = null) {
  const serverTimezone = getServerTimezone()
  const start = moment.tz(startTime, serverTimezone)
  const end = endTime ? moment.tz(endTime, serverTimezone) : getServerNow()
  return Math.floor(end.diff(start) / 1000)
}

/**
 * 检查时间是否为今天（服务器时区）
 * @param {string|Date|moment.Moment} time 要检查的时间
 * @returns {boolean} 是否为今天
 */
export function isServerToday(time) {
  const serverTimezone = getServerTimezone()
  const serverTime = moment.tz(time, serverTimezone)
  const serverNow = getServerNow()
  return serverTime.format('YYYY-MM-DD') === serverNow.format('YYYY-MM-DD')
}

/**
 * 获取服务器时区的时间范围（用于API请求）
 * @param {string|Date|moment.Moment} date 指定日期，默认为今天
 * @returns {object} 包含start和end的对象
 */
export function getServerDateRange(date = null) {
  const targetDate = date || getServerNow()
  return {
    start: getServerDateStart(targetDate),
    end: getServerDateEnd(targetDate)
  }
}

// 默认导出常用函数
export default {
  getServerTimezone,
  getServerNow,
  localToServerTime,
  serverToLocalTime,
  getServerTodayStart,
  getServerTodayEnd,
  getServerDateStart,
  getServerDateEnd,
  formatServerTime,
  formatServerTimeEn,
  getServerTimeDiff,
  isServerToday,
  getServerDateRange
}
